<template>
    <div>
        <div v-if="records.length > 0">
            <div v-if="enableFilter">
                <record-filter-bar :id="id"
                                   :records="this.records"
                                   :record-fields-to-filter="this.recordFieldsToDisplay">
                </record-filter-bar>
            </div>

            <div v-if="sortedRecords.length > 0">
                <div v-if="deleteAllFilesUri !== null" class="mb-3">
                    <delete-record-button :endpoint="deleteAllFilesUri"
                                          :redirect-url="redirectAfterDelete()"
                                          :show-confirmation-dialog="true"
                                          :confirmation-dialog-title="translate('RecordManager.' + recordType + '.delete_all_files')"
                                          :confirmation-dialog-message="translate('RecordManager.' + recordType + '.delete_all_files_confirm')">
                        <i class="fas fa-trash-alt"></i> {{ translate('RecordManager.' + recordType + '.delete_all_files') }}
                    </delete-record-button>                  
                </div>
                <table class="table table-striped table-bordered table-sm">
                    <thead>
                        <tr class="thead-dark">
                            <th class="text-center cell-sm">
                                <i class="fa fa-download" :title="translate('RecordManager.' + recordType + '.download_file')"></i>                
                            </th>

                            <th v-if="showThumbnail">
                                {{ translate('RecordManager.' + recordType + '.thumbnail_uri') }}
                            </th>

                            <th v-for="(fieldName, index) in recordFieldsToDisplay" :class="[sortField == fieldName ? 'active-sort' : '']" @click="setSortFieldAndDirection(fieldName)" :key="index">
                                <span class="sort-records">
                                    <i v-if="sortField == fieldName && !reverseSort" class="fa fa-sort-asc active-sort"></i>
                                    <i v-else-if="sortField == fieldName && reverseSort" class="fa fa-sort-desc active-sort"></i>
                                    <i v-else class="fa fa-sort"></i>
                                </span>
                                {{ translate('RecordManager.' + recordType + '.' + fieldName) }}
                            </th>

                            <th v-if="deleteFileBaseUri !== null" class="text-center">
                                <i class="fas fa-tras-alt" title="Delete file"></i>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(record, index) in sortedRecords" :key="index">
                            <td class="text-center cell-sm">
                                <a :href="downloadFileBaseUri + '/' + record['filename']" class="btn btn-secondary btn-sm" :title="translate('RecordManager.' + recordType + '.download_file')" target="_blank">
                                    <i class="fa fa-download"></i>
                                </a>
                            </td>

                            <td v-if="showThumbnail">
                                <image-view v-if="record['file_extension'] !== 'pdf'"
                                    :image-server-base-path="record['image_server_base_path']"
                                    :filename="record['filename']"
                                    :label="record['label']"
                                    :width="record['width']"
                                    :scale-factor="record['scale_factor']"
                                    :modification-time="record['modification_time']"
                                    :link="record['image_server_base_path'] + '/' + record['filename'] + '/full/full/0/default.jpg'"
                                    link-target="_blank">
                                </image-view>
                                <div v-else>
                                    <pdf :src="record['pdf_src']" :style="{ 'width': record['width'] * record['scale_factor'] + 'px' }"></pdf>
                                    {{ record['label'] }}
                                </div>
                                
                            </td>

                            <td v-for="(fieldName, index) in recordFieldsToDisplay" :key="index"> 
                                <span v-html="record[fieldName]"></span> 
                            </td>
    
                            <td v-if="deleteFileBaseUri !== null" class="text-center">
                                <delete-record-button :endpoint="deleteFileBaseUri + '/' + record['filename']"
                                                      :redirect-url="redirectAfterDelete()"
                                                      :show-confirmation-dialog="true"
                                                      :confirmation-dialog-title="translate('RecordManager.' + recordType + '.delete_file')"
                                                      :confirmation-dialog-message="translate('RecordManager.' + recordType + '.delete_file_confirm')">
                                    <i class="fas fa-trash-alt"></i>
                                </delete-record-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p v-else class="alert alert-info">
                No records matched your filter text: <strong>{{ this.filterQuery }}</strong>
            </p>
        </div>
        <p v-else>
            No {{ recordType | pluralize(recordType) | injectWhitespace | capitalizeWords }} have been added.
        </p>
    </div>
</template>

<script>
    import pdf from 'vue-pdf'
    export default {
        components: {
            pdf
        },
        
        props: {
            records: { type: Array, required: true },
            recordType: { type: String, required: true },
            downloadFileBaseUri: { type: String, required: true },
            deleteFileBaseUri: { type: String, required: false, default: null },
            deleteAllFilesUri: { type: String, required: false, default: null },
            redirectAfterDeleteUri: { type: String, required: false, default: null },
            recordFieldsToDisplay: { type: Array, required: false },
            enableFilter: { type: Boolean, required: false, default: true },
            showThumbnail: { type: Boolean, required: false, default: false },
            defaultSortField: { type: String, required: false, default: null },
            defaultSortDirection: { type: String, required: false, default: 'asc' }
        },

        data() {
            return {
                id: this._uid,
                filterQuery: '',
                filteredRecords: this.records,
                sortField: this.defaultSortField,
                reverseSort: this.defaultSortDirection !== 'asc'
            }
        },

        computed: {
            sortedRecords() {
                // set the sort field and sort direction
                let _this = this;
                function compare(a, b) {
                    if(a[_this.sortField] < b[_this.sortField]) {
                        return _this.reverseSort ? 1 : -1;
                    }
                    if(a[_this.sortField] > b[_this.sortField]) {
                        return _this.reverseSort ? -1 : 1;
                    }
                    return 0;
                }
                return this.filteredRecords.sort(compare);
            }
        },

        created() {
            EventDispatcher.listen('filterRecordsByFilterQuery' + this.id, (payload) => {
                this.filterQuery = payload.filterQuery;
                this.filteredRecords = payload.filteredRecords;
            });
        },

        methods: {
            setSortFieldAndDirection(fieldName) {
                this.reverseSort = fieldName == this.sortField ? !this.reverseSort : false;
                this.sortField = fieldName; 
            },

            redirectAfterDelete() {
                return this.redirectAfterDeleteUri;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .table-bordered {
        thead th {
            white-space: nowrap !important;
            padding-right: 10px;
            cursor: pointer;
            /* prevent selecting text on double click when sorting table */
            -webkit-user-select: none; /* Chrome/Safari */        
            -moz-user-select: none; /* Firefox */
            -ms-user-select: none; /* IE10+ */
            &.active-sort {
                background-color: #777;
                color: white;
                font-weight: normal;
            }
            .sort-records {
                padding-right: 5px;
                i.active-sort {
                    color: #fff;
                }
            }
        }

        td {
            border: 1px solid #ddd;
            div.thumbnail {
                display: flex;
                align-items: center;
                justify-content: center;
                img {
                    border: 1px solid #ddd;
                    cursor: -moz-zoom-in; 
                    cursor: -webkit-zoom-in; 
                    cursor: zoom-in;
                }
            }
        }
    }
    
    .cell-sm {
        width: 5%;
    }
</style>